import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import { apiPost } from '../../../utils/apiClient';
import { addReportToServer } from '../../../functions/sendReport';
import { HALLOWEEN_MSG_KEY, halloweenUniqueMsg } from '../../../utils/halloweenMsg';
import {
  getHalloweenPlayUrl,
  isAllowedHalloweenScoreOrigin,
} from '../../../utils/halloweenPlayUrl';
import './HalloweenGamePage.css';

const PLANS_URL = 'https://extramileplay.com/plans';
const DEMO_SUBSCRIBE_TITLE = 'Subscribe to any plan to enjoy the games.';

function pad2(n) {
  return n > 9 ? String(n) : `0${n}`;
}

function formatHMS(hours, minutes, seconds) {
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`;
}

function getBaseRedirect(user) {
  return (
    user?.backButtonRedirect
    || process.env.REACT_APP_BASE_URL
    || '/'
  );
}

function leaveGame(user, isFromMobileApp) {
  if (isFromMobileApp) {
    window.GameStatus?.postMessage?.('navigate_back');
    return;
  }
  window.location.href = getBaseRedirect(user);
}

function isDemoUser(user) {
  if (!user) return false;
  return (
    user.sessionId === 'demobypass'
    || user.role === 'demobypass'
    || user.source === 'DEMO'
  );
}

function withDemoPlayUrl(baseUrl, isDemo) {
  if (!isDemo || !baseUrl) return baseUrl;
  try {
    const u = new URL(baseUrl, window.location.origin);
    u.searchParams.set('demo', '1');
    return u.toString();
  } catch {
    return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}demo=1`;
  }
}

export default function HalloweenGamePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const user = useSelector((s) => s.auth.user);
  const isDemo = isDemoUser(user);
  const playUrl = useMemo(
    () => withDemoPlayUrl(getHalloweenPlayUrl(), isDemo),
    [isDemo]
  );
  const iframeRef = useRef(null);
  const isFromMobileApp = searchParams.get('fromMobileApp') === 'true'
    || (() => {
      try {
        return sessionStorage.getItem('hf_fromMobileApp') === 'true';
      } catch {
        return false;
      }
    })();
  const [ready, setReady] = useState(false);
  const timerRef = useRef({ hours: 0, minutes: 0, seconds: 0 });
  const timeStrRef = useRef('00:00:00');
  const savingRef = useRef(false);
  const demoLimitHandledRef = useRef(false);
  const intervalRef = useRef(null);

  // Already-played is enforced on the login page only.
  useEffect(() => {
    if (!user?.userId) return;
    setReady(true);
  }, [user]);

  useEffect(() => {
    if (!ready) return undefined;

    timerRef.current = { hours: 0, minutes: 0, seconds: 0 };
    timeStrRef.current = '00:00:00';

    intervalRef.current = setInterval(() => {
      const t = timerRef.current;
      t.seconds += 1;
      if (t.seconds >= 60) {
        t.seconds = 0;
        t.minutes += 1;
        if (t.minutes >= 60) {
          t.minutes = 0;
          t.hours += 1;
        }
      }
      timeStrRef.current = formatHMS(t.hours, t.minutes, t.seconds);
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [ready]);

  async function redirectDemoToPlans() {
    if (demoLimitHandledRef.current) return;
    demoLimitHandledRef.current = true;
    savingRef.current = true;
    await Swal.fire({
      title: DEMO_SUBSCRIBE_TITLE,
      icon: 'success',
      confirmButtonText: 'Close',
      allowOutsideClick: false,
    });
    window.location.href = PLANS_URL;
  }

  useEffect(() => {
    if (!ready || !user?.userId) return undefined;

    async function handleScore(score) {
      if (savingRef.current || demoLimitHandledRef.current) return;
      savingRef.current = true;

      const playTime = timeStrRef.current || '00:00:00';

      try {
        const saved = await apiPost('/halloween/saveScore', {
          userId: user.userId,
          score,
          time: playTime,
          email: user.email || '',
          name: user.name || '',
          user_type: user.role || user.source || '',
        });

        // Race / deep-link guard — primary check is on login
        if (saved.alreadyPlayed) {
          await Swal.fire({
            title: "You've already played the game",
            text: 'Redirecting you back…',
            icon: 'info',
            confirmButtonText: 'OK',
          });
          leaveGame(user, isFromMobileApp);
          return;
        }

        if (isDemo) {
          await Swal.fire({
            title: DEMO_SUBSCRIBE_TITLE,
            icon: 'success',
            confirmButtonText: 'Close',
            allowOutsideClick: false,
          });
          window.location.href = PLANS_URL;
          return;
        }

        if (saved.uniqueMsg) {
          sessionStorage.setItem(HALLOWEEN_MSG_KEY, saved.uniqueMsg);
        } else if (saved.ok) {
          sessionStorage.setItem(HALLOWEEN_MSG_KEY, halloweenUniqueMsg(score, playTime));
        }

        if (saved.inserted) {
          await addReportToServer(user, {
            points: Math.floor(Number(score) || 0),
            reportTime: playTime,
            answers: ['halloween_town', String(score)],
          });
        }

        navigate(`/thank-you${isFromMobileApp ? '?fromMobileApp=true' : ''}`, { replace: true });
      } catch (err) {
        console.error('halloween save failed', err);
        savingRef.current = false;
        await Swal.fire({
          title: 'Your session has timed out',
          text: 'Please start again',
          icon: 'error',
        });
        navigate(`/${isFromMobileApp ? '?fromMobileApp=true' : ''}`, { replace: true });
      }
    }

    function onMessage(event) {
      if (!isAllowedHalloweenScoreOrigin(event.origin)) return;
      const data = event.data;
      if (!data || typeof data !== 'object') return;

      if (data.type === 'halloween-demo-limit') {
        redirectDemoToPlans();
        return;
      }

      if (data.type === 'halloween-score') {
        handleScore(data.score);
      }
    }

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [ready, user, navigate, isFromMobileApp, isDemo]);

  const onIframeLoad = () => {
    if (!isDemo) return;
    try {
      iframeRef.current?.contentWindow?.postMessage(
        { type: 'halloween-set-demo', isDemo: true },
        '*'
      );
    } catch {
      /* cross-origin S3: query ?demo=1 on src is enough */
    }
  };

  if (!user?.userId) {
    return (
      <div className="halloween-game-page">
        <p className="halloween-game-loading">Loading…</p>
      </div>
    );
  }

  if (!ready) return null;

  return (
    <div className="halloween-game-page">
      <div className="halloween-game-frame-wrap">
        <iframe
          ref={iframeRef}
          title="Halloween Town"
          className="halloween-game-iframe"
          src={playUrl}
          allow="autoplay; fullscreen"
          onLoad={onIframeLoad}
        />
      </div>
    </div>
  );
}
